/**
 * USE CLIENTES - Hooks para gestión de clientes
 *
 * Hooks de TanStack Query para:
 * - Listar clientes (useQuery con paginación)
 * - Buscar clientes (autocompletado)
 * - CRUD mutations
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-client";
import {
  getClientes,
  getCliente,
  createCliente,
  updateCliente,
  deleteCliente,
  activateCliente,
  deactivateCliente,
  buscarClientes,
  type Cliente,
  type ClienteCreateData,
  type ClienteUpdateData,
  type ClienteFilters,
} from "@/services/clientes";

// Re-export functions and constants for useServerDataTable
export { getClientes, TIPOS_CLIENTE } from "@/services/clientes";

// Re-export types
export type {
  Cliente,
  ClienteListItem,
  ClienteCreateData,
  ClienteUpdateData,
  ClienteFilters,
  TipoCliente,
} from "@/services/clientes";

// =============================================================================
// QUERIES
// =============================================================================

export function useClientes(filters?: ClienteFilters, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.clientes.list(filters || {}),
    queryFn: () => getClientes(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useCliente(id: number, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.clientes.detail(id),
    queryFn: () => getCliente(id),
    enabled: options?.enabled ?? true,
  });
}

export function useBuscarClientes(query: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.clientes.search(query),
    queryFn: () => buscarClientes(query),
    enabled: (options?.enabled ?? true) && query.length >= 2,
  });
}

// =============================================================================
// MUTATIONS
// =============================================================================

interface CreateCallbacks {
  onSuccess?: (cliente: Cliente) => void;
  onError?: (error: Error) => void;
}

interface MutationCallbacks {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}

export function useCreateCliente(callbacks?: CreateCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ClienteCreateData) => createCliente(data),
    onSuccess: (cliente) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.all });
      callbacks?.onSuccess?.(cliente);
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useUpdateCliente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ClienteUpdateData }) => updateCliente(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeleteCliente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteCliente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useActivateCliente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => activateCliente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}

export function useDeactivateCliente(callbacks?: MutationCallbacks) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deactivateCliente(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.clientes.all });
      callbacks?.onSuccess?.();
    },
    onError: (error: Error) => callbacks?.onError?.(error),
  });
}
